import React from 'react';
import { Button } from "@/components/ui/button";
import { Accessibility } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useLanguage } from '@/contexts/LanguageContext';

const AccessibilityMenu = () => {
  const [contrastMode, setContrastMode] = useLocalStorage<'normal' | 'high'>('accessibility_contrast', 'normal');
  const [colorBlindMode, setColorBlindMode] = useLocalStorage<'none' | 'protanopia' | 'deuteranopia' | 'tritanopia'>('accessibility_colorblind', 'none');
  const [textSize, setTextSize] = useLocalStorage<'normal' | 'large' | 'x-large'>('accessibility_textsize', 'normal');
  const { t } = useLanguage();

  React.useEffect(() => {
    const html = document.documentElement;
    
    html.classList.remove(
      'high-contrast', 
      'color-protanopia', 
      'color-deuteranopia', 
      'color-tritanopia',
      'text-large',
      'text-x-large'
    );
    
    if (contrastMode === 'high') {
      html.classList.add('high-contrast');
    }
    
    if (colorBlindMode !== 'none') {
      html.classList.add(`color-${colorBlindMode}`);
    }
    
    if (textSize !== 'normal') {
      html.classList.add(`text-${textSize}`);
    }
  }, [contrastMode, colorBlindMode, textSize]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="rounded-full w-9 h-9 p-0"
          title="Options d'accessibilité"
          aria-label="Options d'accessibilité"
        >
          <Accessibility className="h-5 w-5" />
          <span className="sr-only">Options d'accessibilité</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Accessibilité</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Contraste</DropdownMenuLabel>
        <DropdownMenuItem 
          onClick={() => setContrastMode('normal')}
          className={contrastMode === 'normal' ? 'bg-muted font-bold' : ''}
        >
          Normal
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setContrastMode('high')}
          className={contrastMode === 'high' ? 'bg-muted font-bold' : ''}
        >
          Contraste élevé
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Mode daltonien</DropdownMenuLabel>
        <DropdownMenuItem 
          onClick={() => setColorBlindMode('none')}
          className={colorBlindMode === 'none' ? 'bg-muted font-bold' : ''}
        >
          Aucun
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setColorBlindMode('protanopia')}
          className={colorBlindMode === 'protanopia' ? 'bg-muted font-bold' : ''}
        >
          Protanopie (rouge-vert)
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setColorBlindMode('deuteranopia')}
          className={colorBlindMode === 'deuteranopia' ? 'bg-muted font-bold' : ''}
        >
          Deutéranopie (vert)
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setColorBlindMode('tritanopia')}
          className={colorBlindMode === 'tritanopia' ? 'bg-muted font-bold' : ''}
        >
          Tritanopie (bleu-jaune)
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Taille du texte</DropdownMenuLabel>
        <DropdownMenuItem 
          onClick={() => setTextSize('normal')}
          className={textSize === 'normal' ? 'bg-muted font-bold' : ''}
        >
          Normal
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setTextSize('large')}
          className={textSize === 'large' ? 'bg-muted font-bold' : ''}
        >
          Grand
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setTextSize('x-large')}
          className={textSize === 'x-large' ? 'bg-muted font-bold' : ''}
        >
          Très grand
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccessibilityMenu;
